/**
 * THE TAB'S OWN WRITES, by transaction id (query speed Q11, spec
 * docs/specs/bms/2026-11-01-001-query-speed.md §5.5).
 *
 * A BMS write's response carries the ids of the transactions it appended in
 * (`x-cova-xid`, set by the session lane). The app's fetch delegate notes them
 * here (`src/shared/scope/app-http-client.ts`), and the change-feed poller sends
 * the recent ones with each poll. The feed then reports a table as `echoed`
 * only when every change to it in the window was one of these, and the poller
 * leaves those tables alone: the save already refetched what it changed. A
 * co-worker's change in the same window is never echoed, so it still refetches.
 *
 * BOUNDED BOTH WAYS. At most `OWN_WRITES_MAX` ids, the newest kept, which is
 * also the contract's cap on a poll; and none older than `OWN_WRITES_TTL_MS`,
 * well past the 3 s beat, so a backlog after a hidden spell still finds its
 * own saves, while an id that can no longer be in any window stops riding
 * every poll. Forgetting one early is safe: its table is refetched, as before.
 *
 * THE INVARIANT EVERY SAVE KEEPS (round 1 should-fix 9). Skipping an echoed
 * table is right only because the save's own invalidation already reached
 * every reader of every table its transaction wrote. A save that narrows its
 * refetch must still read what nothing else will, and access tables are never
 * skipped (`use-live-invalidation.ts`), since a grant moves identity, which no
 * save refreshes. `agent_docs/frontend/mutation-cache-invalidation.md` says it
 * for the writers.
 *
 * Module state, one per tab, never in browser storage: a transaction id is
 * only this tab's to name. It is never forgotten on a firm change, and need
 * not be (round 1 nit 12): a transaction id is unique across the database, so
 * an id from the previous firm can only ever match the tab's own write.
 */

import { isXid8Text, WRITE_ECHO_HEADER } from '@/contexts/audit/changes/domain/changes-page'

/** The response header a BMS write names its transaction ids in: the feed contract's one name. */
export const OWN_WRITES_HEADER = WRITE_ECHO_HEADER

/** The most ids a poll names; the same number caps `changesSince`'s payload. */
export const OWN_WRITES_MAX = 64

/** How long an id is named after its write answered. */
export const OWN_WRITES_TTL_MS = 5 * 60_000


let noted: ReadonlyArray<{ readonly xid: string; readonly at: number }> = []

/** Note the ids in one `x-cova-xid` header value. Anything that is not an id is ignored. */
export const noteOwnWrites = (header: string | null, now: number = Date.now()): void => {
  if (header === null) return
  const fresh = header
    .split(',')
    .map((part) => part.trim())
    .filter((xid) => isXid8Text(xid))
  if (fresh.length === 0) return
  const kept = noted.filter((entry) => !fresh.includes(entry.xid) && now - entry.at < OWN_WRITES_TTL_MS)
  noted = [...kept, ...fresh.map((xid) => ({ xid, at: now }))].slice(-OWN_WRITES_MAX)
}

/** The ids to name on the next poll, oldest first. */
export const ownWriteXids = (now: number = Date.now()): ReadonlyArray<string> =>
  noted.filter((entry) => now - entry.at < OWN_WRITES_TTL_MS).map((entry) => entry.xid)

/** Forget every id (a test's reset). */
export const forgetOwnWrites = (): void => {
  noted = []
}
